import * as pdfjsLib from 'pdfjs-dist';

// Set worker path - use CDN for simplicity
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export interface ParsedChapter {
  title: string;
  content: string;
  sectionType: 'chapter' | 'prologue' | 'epilogue' | 'introduction' | 'preface';
}

export interface ParsedBook {
  chapters: ParsedChapter[];
  coverImage?: string;
  title?: string;
  author?: string;
}

// Chapter detection patterns
const chapterPatterns = [
  /^chapter\s+(\d+|[ivxlcdm]+)\s*[:\-–—]?\s*/i,
  /^ch\.?\s*(\d+)\s*[:\-–—]?\s*/i,
  /^(\d+)\.\s+/,
  /^part\s+(\d+|[ivxlcdm]+)\s*[:\-–—]?\s*/i,
];

const sectionPatterns: Record<string, RegExp> = {
  prologue: /^prologue\s*[:\-–—]?\s*/i,
  epilogue: /^epilogue\s*[:\-–—]?\s*/i,
  introduction: /^introduction\s*[:\-–—]?\s*/i,
  preface: /^preface\s*[:\-–—]?\s*/i,
};

function detectSectionType(title: string): ParsedChapter['sectionType'] {
  const lowerTitle = title.toLowerCase().trim();

  for (const [type, pattern] of Object.entries(sectionPatterns)) {
    if (pattern.test(lowerTitle)) {
      return type as ParsedChapter['sectionType'];
    }
  }

  return 'chapter';
}

function isChapterHeading(line: string): boolean {
  const trimmed = line.trim();

  // Check chapter patterns
  for (const pattern of chapterPatterns) {
    if (pattern.test(trimmed)) {
      return true;
    }
  }

  // Check section patterns
  for (const pattern of Object.values(sectionPatterns)) {
    if (pattern.test(trimmed)) {
      return true;
    }
  }

  return false;
}

function cleanChapterTitle(text: string): string {
  let title = text.trim();

  // Remove chapter number patterns
  for (const pattern of chapterPatterns) {
    title = title.replace(pattern, '').trim();
  }
  for (const pattern of Object.values(sectionPatterns)) {
    title = title.replace(pattern, '').trim();
  }

  return title;
}

async function extractCoverFromFirstPage(
  pdf: pdfjsLib.PDFDocumentProxy
): Promise<string | undefined> {
  try {
    const page = await pdf.getPage(1);
    const scale = 2; // Higher scale for better quality
    const viewport = page.getViewport({ scale });

    // Create canvas for rendering
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return undefined;

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Render page to canvas
    await page.render({
      canvasContext: context,
      viewport,
      canvas,
    } as any).promise;

    // Check if page has significant content (images) vs just text
    // by analyzing pixel data
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    let colorVariance = 0;
    let lastR = 0, lastG = 0, lastB = 0;

    // Sample pixels to detect if page is image-heavy
    for (let i = 0; i < pixels.length; i += 400) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];

      colorVariance += Math.abs(r - lastR) + Math.abs(g - lastG) + Math.abs(b - lastB);
      lastR = r;
      lastG = g;
      lastB = b;
    }

    // If there's significant color variance, it's likely an image cover
    const avgVariance = colorVariance / (pixels.length / 400);
    if (avgVariance > 15) {
      return canvas.toDataURL('image/jpeg', 0.85);
    }

    return undefined;
  } catch {
    return undefined;
  }
}

export async function parsePdf(file: File): Promise<ParsedBook> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const numPages = pdf.numPages;
  const chapters: ParsedChapter[] = [];
  let coverImage: string | undefined;

  // Try to extract cover from first page
  coverImage = await extractCoverFromFirstPage(pdf);

  // Determine starting page (skip first if it's a cover)
  const startPage = coverImage ? 2 : 1;

  let currentChapter: { title: string; content: string[]; sectionType: ParsedChapter['sectionType'] } | null = null;
  let chapterCount = 0;
  let previousLine = '';

  for (let pageNum = startPage; pageNum <= numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    // Extract text with proper line breaks
    const lines: string[] = [];
    let currentLine = '';
    let lastY: number | null = null;

    for (const item of textContent.items) {
      if ('str' in item) {
        const text = item.str;
        const transform = item.transform;
        const y = transform[5];

        // Detect new line based on Y position change
        if (lastY !== null && Math.abs(y - lastY) > 5) {
          if (currentLine.trim()) {
            lines.push(currentLine.trim());
          }
          currentLine = '';
        }

        currentLine += text;
        lastY = y;
      }
    }

    // Don't forget the last line
    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }

    // Process lines to find chapters
    for (const line of lines) {
      const trimmedLine = line.trim();

      // Skip empty lines
      if (!trimmedLine) continue;

      // Check for chapter heading
      if (isChapterHeading(trimmedLine)) {
        // Save previous chapter
        if (currentChapter && currentChapter.content.length > 0) {
          // Convert content array to HTML paragraphs
          const htmlContent = currentChapter.content
            .map(p => `<p>${p}</p>`)
            .join('');

          chapters.push({
            title: currentChapter.title,
            content: htmlContent,
            sectionType: currentChapter.sectionType,
          });
        }

        // Start new chapter
        chapterCount++;
        const sectionType = detectSectionType(trimmedLine);
        let title = cleanChapterTitle(trimmedLine);

        // If title is empty after cleaning, use default
        if (!title) {
          title = sectionType === 'chapter'
            ? `Chapter ${chapterCount}`
            : sectionType.charAt(0).toUpperCase() + sectionType.slice(1);
        }

        // Check if next line might be the actual title (common in books)
        currentChapter = {
          title,
          content: [],
          sectionType,
        };
      } else if (currentChapter) {
        // Add line to current chapter
        // Check if this might be a continuation of chapter title
        if (currentChapter.content.length === 0 && previousLine.match(/^chapter\s+\d+$/i)) {
          // This line might be the actual chapter title
          currentChapter.title = trimmedLine;
        } else {
          currentChapter.content.push(trimmedLine);
        }
      } else {
        // Content before first chapter - create initial chapter
        chapterCount++;
        currentChapter = {
          title: `Chapter ${chapterCount}`,
          content: [trimmedLine],
          sectionType: 'chapter',
        };
      }

      previousLine = trimmedLine;
    }
  }

  // Save last chapter
  if (currentChapter && currentChapter.content.length > 0) {
    const htmlContent = currentChapter.content
      .map(p => `<p>${p}</p>`)
      .join('');

    chapters.push({
      title: currentChapter.title,
      content: htmlContent,
      sectionType: currentChapter.sectionType,
    });
  }

  // If no chapters found, create single chapter
  if (chapters.length === 0) {
    // Extract all text as single chapter
    let allContent: string[] = [];

    for (let pageNum = startPage; pageNum <= numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .filter(item => 'str' in item)
        .map(item => (item as { str: string }).str)
        .join(' ');

      if (pageText.trim()) {
        allContent.push(pageText);
      }
    }

    if (allContent.length > 0) {
      chapters.push({
        title: 'Chapter 1',
        content: `<p>${allContent.join('</p><p>')}</p>`,
        sectionType: 'chapter',
      });
    }
  }

  // Extract title from filename
  const fileName = file.name.replace(/\.pdf$/i, '');
  const title = fileName.replace(/[-_]/g, ' ').trim();

  // Try to extract metadata
  let author: string | undefined;
  try {
    const metadata = await pdf.getMetadata();
    if (metadata.info) {
      const info = metadata.info as Record<string, string>;
      if (info.Author) author = info.Author;
      if (info.Title && info.Title.length < 200) {
        // Use PDF metadata title if available
      }
    }
  } catch {
    // Ignore metadata errors
  }

  return {
    chapters,
    coverImage,
    title: title || undefined,
    author,
  };
}
